'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { useRef } from 'react';
import { GREEN_MOVING_BLOB_COLORS, GREEN_MOVING_BLOB_SIZES, GREEN_STATIC_BLOB_COLORS, GREEN_STATIC_BLOB_SIZES, GREEN_STATIC_QUAD_POSITIONS, PURPLE_BLOB_COLORS, STATIC_BLOB_POSITIONS } from '@/components/AnimatedBackground/const';
import { AnimatedBackgroundTheme, StaticBlobsPosition } from '@/components/AnimatedBackground/types';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
interface AnimatedBackgroundProps {
  className?: string;
  staticBlobsPosition?: StaticBlobsPosition;
  theme?: AnimatedBackgroundTheme;
  brightColors?: boolean;
}
export const AnimatedBackground = t0 => {
  const $ = _c(23);
  const {
    className,
    staticBlobsPosition: t1,
    theme: t2,
    brightColors: t3
  } = t0;
  const staticBlobsPosition = t1 === undefined ? "top-right_bottom-left" : t1;
  const theme = t2 === undefined ? "purple" : t2;
  const brightColors = t3 === undefined ? false : t3;
  const containerRef = useRef(null);
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const mm = gsap.matchMedia();
      mm.add({
        isDesktop: "(min-width: 992px)",
        isMobile: "(max-width: 991px)"
      }, ctx => {
        const {
          isMobile
        } = ctx.conditions as {
          isMobile: boolean;
        };
        if (!containerRef.current) {
          return;
        }
        const movingBlobs = containerRef.current.querySelectorAll(`.${css.movingBlob}`);
        const blobTimelines = [];
        movingBlobs.forEach(blob => {
          const startX = 20 + Math.random() * 60;
          const startY = 20 + Math.random() * 60;
          gsap.set(blob, {
            left: `${startX}%`,
            top: `${startY}%`
          });
          if (isMobile) {
            return;
          }
          const createAnimation = () => {
            const duration = 15 + Math.random() * 10;
            const points = 3 + Math.floor(Math.random() * 2);
            const timeline = gsap.timeline({
              repeat: -1,
              yoyo: false
            });
            blobTimelines.push(timeline);
            for (let i = 0; i < points; i++) {
              const x = 15 + Math.random() * 70;
              const y = 15 + Math.random() * 70;
              timeline.to(blob, {
                left: `${x}%`,
                top: `${y}%`,
                duration: duration / points,
                ease: "sine.inOut"
              });
            }
            timeline.to(blob, {
              left: `${startX}%`,
              top: `${startY}%`,
              duration: duration / points,
              ease: "sine.inOut"
            });
          };
          createAnimation();
        });
        const observer = new IntersectionObserver(t6 => {
          const [entry] = t6;
          blobTimelines.forEach(tl => tl.paused(!entry.isIntersecting));
        }, {
          threshold: 0
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
      });
      return () => mm.revert();
    };
    t5 = {
      scope: containerRef
    };
    $[0] = t4;
    $[1] = t5;
  } else {
    t4 = $[0];
    t5 = $[1];
  }
  useGSAP(t4, t5);
  const positions = STATIC_BLOB_POSITIONS[staticBlobsPosition];
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== brightColors || $[3] !== className || $[4] !== positions[0] || $[5] !== positions[1] || $[6] !== staticBlobsPosition || $[7] !== theme) {
    const purpleBlobs = [{
      isStatic: true,
      ...positions[0],
      size: "max(52vw, 600px)",
      color: PURPLE_BLOB_COLORS[2]
    }, {
      isStatic: true,
      ...positions[1],
      size: "max(59vw, 650px)",
      color: PURPLE_BLOB_COLORS[2]
    }, {
      isStatic: false,
      size: "max(43vw, 400px)",
      color: PURPLE_BLOB_COLORS[0]
    }, {
      isStatic: false,
      size: "max(46vw, 450px)",
      color: PURPLE_BLOB_COLORS[1]
    }, {
      isStatic: false,
      size: "max(40vw, 350px)",
      color: PURPLE_BLOB_COLORS[3]
    }];
    const greenQuad = GREEN_STATIC_QUAD_POSITIONS[staticBlobsPosition];
    const greenBlobs = [...GREEN_STATIC_BLOB_COLORS.map((color, i_0) => ({
      isStatic: true,
      ...greenQuad[i_0],
      size: GREEN_STATIC_BLOB_SIZES[i_0],
      color
    })), ...GREEN_MOVING_BLOB_COLORS.map(_temp)];
    const blobs = theme === "green" ? greenBlobs : purpleBlobs;
    const blobBackground = _temp2;
    t8 = containerRef;
    const t10 = theme === "green" && css.greenTheme;
    const t11 = brightColors && css.brightColors;
    if ($[12] !== className || $[13] !== t10 || $[14] !== t11) {
      t9 = clsx(css.container, t10, t11, className);
      $[12] = className;
      $[13] = t10;
      $[14] = t11;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    t6 = css.blobsContainer;
    t7 = blobs.map((blob_0, index) => <div key={index} className={blob_0.isStatic ? css.staticBlob : css.movingBlob} style={{
      width: blob_0.size,
      height: blob_0.size,
      background: blobBackground(blob_0.color),
      ...(blob_0.isStatic && {
        left: (blob_0 as {
          left: string;
        }).left,
        top: (blob_0 as {
          top: string;
        }).top
      })
    }} />);
    $[2] = brightColors;
    $[3] = className;
    $[4] = positions[0];
    $[5] = positions[1];
    $[6] = staticBlobsPosition;
    $[7] = theme;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t7) {
    t10 = <div className={t6}>{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = <div ref={t8} className={t9}>{t10}</div>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
function _temp(color_0, i_1) {
  return {
    isStatic: false,
    size: GREEN_MOVING_BLOB_SIZES[i_1],
    color: color_0
  };
}
function _temp2(color_1) {
  return `radial-gradient(circle at center, ${color_1} 0%, ${color_1}00 70%)`;
}